<template lang="pug">
y-dialog(v-if="showDialog" v-model="showDialog" persistent fullscreen scrollable)
  v-card.neutral200
    v-card-text.neutral700--text.pa-8(outlined)
      div.d-flex.align-center.mb-6
        v-btn.mr-2(icon color="neutral700" @click="closeDialog")
          v-icon(color="neutral700" :size="24") mdi-arrow-left
        .text-2xl.font-semibold Add customers into segment
      v-card.rounded-lg.content-box.pa-6(outlined)
        div.py-6.px-10.round-lg.white(v-if="isFetching")
          v-skeleton-loader(type="list-item-three-line@5")
        template(v-else)
          div.font-medium.neutral700--text Select the customers you want to add to the segment
          div.text-sm.neutral700--text Mark the checkbox to select the customer
          v-text-field.text-sm.round-xl.mt-4.search-field(v-model="searchText" placeholder="Search customer" outlined dense clearable height="48" hide-details="auto" @input="queryChanged")

          v-tabs.mt-4(v-model="tabs" color="basic700" :height="44" show-arrows active-class="font-semibold")
            v-tab.text-sm.font-medium.text-none All {{ totalCount ? `(${totalCount})` : '' }}
            v-tab.text-sm.font-medium.text-none Selected ({{ selectedCustomers ? selectedCustomers.length : '0' }})

          v-divider
          patients-filter-row.my-4(:filters.sync="filters" ref="filter")
          .data-table
            v-data-table.mt-sm-4.pb-6(:headers="headers" :items="customerList" item-key="id" :items-per-page="pageSize" hide-default-footer :loading="isFetchingCustomers" :header-props="{ sortIcon: '$yzi-triangle' }" @click:row="selectCustomer")
              template(v-slot:no-data)
                .mt-16
                  v-img.mx-auto(src="@/assets/images/icon_empty.png" width=80)
                  .mt-2.p2.basic600--text {{ $t('no_list_available') }}
              template(v-slot:header.checked)
                yc-checkbox(v-if="!tabs" v-model="checkAll" @change="onCheckedAll")
              template(#item.checked="{ item, headers, index }")
                yc-checkbox(v-if="customerIncluded(item)" indeterminate disabled)
                yc-checkbox(v-else v-model="item.checked" @change="onChecked(item)")
              template(#item.name="{ item, headers, index }")
                div.d-flex.my-3(style="max-width: 200px;")
                  div(style="width: 30px;")
                    v-responsive(:aspect-ratio="7/10" style="width: 30px;")
                      v-card.fill-height(v-if="item.profile_image && item.profile_image.file" flat)
                        v-img(:src="`https://img.yezza.io/50x50/${item.profile_image.path}`" height="100%" width="100%")
                          template(v-slot:placeholder)
                            v-row(class="fill-height ma-0" align="center" justify="center")
                              v-progress-circular(indeterminate color="grey")
                      v-card.neutral300.fill-height(v-else flat)
                        div.d-flex.align-center.justify-center.fill-height
                          v-icon(size="20") $yzi-user-03
                  div.pl-2
                    v-tooltip(top color="black" content-class="black tooltip-top-arrow")
                      template(#activator="{ on, attrs }")
                        div(v-bind="attrs" v-on="on" style="max-width: 145px;")
                          .text-sm.font-semibold.neutral700--text.cursor-help.truncate {{ item.name }}
                      div(style="max-width: 600px;")
                        .text-sm.font-semibold {{ item.name }}
                        .text-xs(v-if="item.dob") {{ $options.filters.getAgeByYearMonthDay(item.dob) | displayAgeByYearMonthDay }} old
                    div.d-flex.align-center
                      y-chip.mr-2(v-if="item.urgent" label="Urgent" size="xs" color="red")
                      .text-xs.neutral700--text.no-wrap(v-if="item.dob") {{ $options.filters.getAgeByYearMonthDay(item.dob) | displayAgeByYearMonthDay }} old
              template(#item.created="{ item, headers, index }")
                div.text-xs.font-bold {{ (item.record_date || item.created) | humanizeDate }}
                div.text-xs {{ (item.record_date || item.created) | displayTime2 }}
              template(#item.address="{ item, headers, index }")
                div(v-if="item.address")
                  v-tooltip(top color="black" content-class="black tooltip-top-arrow")
                    template(#activator="{ on, attrs }")
                      .text-sm.neutral700--text.cursor-help(v-if="item.address.city" v-bind="attrs" v-on="on") {{ item.address.city }}
                    div.text-xs(style="max-width: 250px;") {{ getAddressInString(item.address) }}

          div.d-flex.align-center(v-if="!tabs && totalCount > pageSize")
            v-spacer
            yc-pagination-custom(v-model="currentPage" :parentPageSize.sync="pageSize" :count="totalCount" attach menu-top @input="pageChanged")

    v-divider
    v-card-actions.py-4.px-8.white
      v-spacer
      y-button.mr-4(:label="$t('cancel')" underline color="neutral700" size="sm" button-class="px-0" @click="closeDialog")
      y-button(label="Add to segment" text-color="neutral100" color="neutral700" size="sm" dark :loading="isLoading" @click="saveData")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { debounce } from '@/utils'
import PatientsFilterRow from '@/components/clinic/patient/filters/PatientsFilterRow.vue'

export default {
  components: { PatientsFilterRow },
  props: {
    open: {
      type: Boolean,
    },
    segment: {
      required: true,
      type: Object,
      default: null
    },
  },
  data () {
    return {
      isFetching: false,
      isFetchingCustomers: false,
      isLoading: false,
      checkAll: false,
      searchText: null,
      tabs: 0,
      totalCount: 0,
      pageSize: 20,
      currentPage: 1,
      selectedSegment: null,
      customers: [],
      selectedCustomers: [],
      filters: [],
      headers: [
        { value: 'checked', text: '', sortable: false},
        { value: 'name', text: 'name', class: 'text-table-header'},
        { value: 'ic', text: 'id', class: 'text-table-header'},
        { value: 'phone', text: 'phone number', class: 'text-table-header'},
        { value: 'created', text: 'date created', class: 'text-table-header'},
        { value: 'email', text: 'email', class: 'text-table-header'},
        { value: 'address', text: 'address', class: 'text-table-header'},
      ],
    }
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
    showDialog: {
      get () {
        return this.open
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
    customerList () {
      if (this.tabs) {
        return this.selectedCustomers
      } else {
        return this.customers
      }
    }
  },
  watch: {
    filters: {
      deep: true,
      handler () {
        this.selectedPatients = []
        this.currentPage = 1
        this.fetchCustomers()
      }
    },
  },
  mounted () {
    this.fetchSegment()
    this.fetchCustomers()
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchSegment () {
      this.isFetching = true
      this.$axios.get(`/connect/${this.company.slug}/groups/${this.segment.id}`)
        .then(resp => {
          this.selectedSegment = resp.data
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchCustomers () {
      this.checkAll = false,
      this.isFetchingCustomers = true
      let params = {
        page_size: this.pageSize,
        page: this.currentPage
      }
      if (this.searchText) {
        params.search = this.searchText
      }
      this.filters.forEach(filter => {
        params[filter.key] = filter.value
      })
      this.$axios.get(`/clinics/${this.company.slug}/patients`, { params })
        .then(resp => {
          this.totalCount = resp.data.count
          let data = resp.data.results
          data.forEach(customer => {
            if (!customer.age && customer.dob) {
              customer.age = this.getAge(customer.dob)
            }
            customer.checked = false
            if (this.selectedCustomers.length) {
              if (this.selectedCustomers.map(m => m.id).includes(customer.id)) {
                customer.checked = true
              }
            }
          })
          this.customers = data
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetchingCustomers = false
        })
    },
    pageChanged () {
      this.fetchCustomers()
    },
    queryChanged: debounce(function () {
      this.currentPage = 1
      this.fetchCustomers()
    }, 750),
    selectCustomer (item) {
      if (this.customerIncluded(item)) {
        return
      }
      item.checked = !item.checked
      this.onChecked(item)
    },
    onChecked (item) {
      if (item.checked) {
        this.selectedCustomers.push(item)
      } else {
        this.checkAll = false
        this.selectedCustomers = this.selectedCustomers.filter(f => f.id !== item.id)
      }
    },
    onCheckedAll () {
      if (this.checkAll) {
        this.selectedCustomers = this.customers.filter(f => !this.selectedSegment.customer.includes(f.id))
        this.customers.map(m => m.checked = true)
      } else {
        this.selectedCustomers = []
        this.customers.map(m => m.checked = false)
      }
    },
    customerIncluded (item) {
      if (this.selectedSegment.customer.includes(item.id)) {
        return true
      }
      return false
    },
    saveData () {
      this.isLoading = true
      let ids = this.selectedCustomers.map(m => m.id)
      let data = { customer: this.selectedSegment.customer.concat(ids) }
      this.$axios.patch(`/connect/${this.company.slug}/groups/${this.selectedSegment.id}`, data)
        .then(() => {
          this.$emit('updated')
          this.closeDialog()
        })
        .catch(err => {
          let message = this.$t('error_message.unable_to_complete_request')
          if (err.response.data) {
            if (err.response.data.detail) {
              message = err.response.data.detail
            } else {
              let data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
              if (data) { message = data }
            }
          }
          this.showMessage(['error', message])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    getAge(date) {
      var today = new Date();
      var birthDate = new Date(date);
      var age = today.getFullYear() - birthDate.getFullYear();
      var month = today.getMonth() - birthDate.getMonth();
      if (month < 0 ||(month === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },
    getAddressInString (addr) {
      let address = ''
      if (addr.address_1) {
        address += addr.address_1
      } if (addr.address_2) {
        address += `, ${addr.address_2}`
      } if (addr.postcode) {
        address += `, ${addr.postcode}`
      } if (addr.city) {
        address += `, ${addr.city}`
      } if (addr.state) {
        address += `, ${addr.state}`
      } if (addr.country) {
        address += `, ${addr.country}`
      }
      return address
    },
    closeDialog () {
      this.$emit('closed')
      this.showDialog = false
    },
  },
}
</script>

<style lang="scss" scoped>
.content-box {
  border: 1px var(--v-neutral300) solid !important;
}
::v-deep {
  .v-data-table__wrapper table tbody tr:hover{
    cursor: pointer;
    background: var(--v-neutral200) !important; 
  }
  .v-text-field--outlined {
    fieldset {
      border-color: var(--v-neutral400);
    }
  } 
  .error--text {
    color: var(--v-danger500) !important;
    fieldset {
      border-color: var(--v-danger500);
    }
  }
  .search-field {
    .v-input__append-inner {
      margin-top: 12px !important;
    }
  }
}
</style>